import './DescriptionList.scss'
import type { ComponentProps, Key, ReactNode } from 'react'
import { clx } from '../clx'

export interface DescriptionItem {
  term: ReactNode
  details: ReactNode
  /** Decorative icon on the term's line, before its text. */
  icon?: ReactNode
  /** React key; defaults to the item's index. */
  id?: Key
}

export interface DescriptionListProps extends Omit<ComponentProps<'dl'>, 'children'> {
  items: DescriptionItem[]
  /**
   * `stacked`: term above details. `inline`: term and details side by side
   * (1 : 2) from phablet up, stacked below. Default `stacked`.
   */
  layout?: 'stacked' | 'inline'
}

export function DescriptionList({
  items,
  layout = 'stacked',
  className,
  ref,
  ...rest
}: DescriptionListProps) {
  return (
    <dl
      ref={ref}
      {...rest}
      className={clx('description-list', `description-list--as-${layout}`, className)}
    >
      {items.map((item, index) => (
        <div key={item.id ?? index} className="description-list__item">
          <dt
            className={clx(
              'description-list__term',
              item.icon != null && 'description-list__term--has-icon'
            )}
          >
            {item.icon != null && (
              <span className="description-list__icon" aria-hidden="true">
                {item.icon}
              </span>
            )}
            {item.term}
          </dt>
          <dd className="description-list__details">{item.details}</dd>
        </div>
      ))}
    </dl>
  )
}
